import BetModel from "../../src/model/BetModel";
import App from "../App";
import Layout from "../utils/Layout";
import { BitmapMoneyFieldConfig, AbstractBitmapMoneyField } from "./AbstractBitmapMoneyField";
import { IObjectConfig } from "../baseConfigs/layoutConfigShema";
import Configs from "../../../src/vendorConfigs/Configs";
import { Graphics, BitmapText, IBitmapTextStyle } from "pixi.js";
import { CurrencyType } from "../managers/contracts/configurations/IBetConfig";
import { isInteger } from 'lodash'

export class BitmapMoneyField extends AbstractBitmapMoneyField{

    protected moneyText: BitmapText;
    protected centsText: BitmapText;
    protected currencyText: BitmapText;
    private boundsRect: Graphics;
    private money: number;
    private useCoins: boolean;
    private symbol: string = "";

    constructor(config?: BitmapMoneyFieldConfig) {
        super(config);

        // On bitmap fonts custom currency signs won't be shown,
        // because the bitmap font can't have all the possible currency signs in it,
        // so we always hide the identifier.
        config.showIdentifier = false;

        const betConfiguration = Configs.bet;
        if (config.alternative) {
            this.useCoins = betConfiguration.alternativeCurrencyType == CurrencyType.Coins;
        } else {
            this.useCoins = betConfiguration.primaryCurrencyType == CurrencyType.Coins;
        }

        if (this.useCoins) {
            this.symbol = "";
        } else {
            this.symbol = BetModel.currency.symbol;
        }

        this.createBoundsRect();
        this.createTextfields();
        this.fitToBounds();
        this.positionText();
    }

    public createBoundsRect() {
        if (this.boundsRect && this.boundsRect.parent) {
            this.boundsRect.parent.removeChild(this.boundsRect);
            this.boundsRect.destroy();
        }

        this.boundsRect = new Graphics();
        this.boundsRect.beginFill(0x00FF00, 0.5);
        // this.boundsRect.drawRect(0, 0, Layout.scalePixels(this._config.bounds.width), Layout.scalePixels(this._config.bounds.height));
        this.boundsRect.drawRect(0, 0, this._config.bounds.width, this._config.bounds.height);
        this.boundsRect.endFill();
        if (!App.instance.debugBounds) {
            this.boundsRect.alpha = 0;
        }
        this.addChild(this.boundsRect);
    }

    protected createTextfields() {
        let textStyle = {
            fontName:this._config.fontFamily,
            fontSize: this._config.moneyFontSize,
            align: 'left',
            tint: this._config.tint,
        } as Partial<IBitmapTextStyle>;

        this.moneyText = new BitmapText("123", textStyle);


        textStyle = {
            fontName:this._config.fontFamily,
            fontSize:this._config.moneyFontSize * this._config.centsRelativeSize / 100,
            align: 'left',
            tint: this._config.tint
        };


        this.centsText = new BitmapText("123", textStyle);

        textStyle = {
            fontName:this._config.fontFamily,
            fontSize:this._config.moneyFontSize * this._config.currencyRelativeSize / 100,
            align: 'left',
            tint: this._config.tint
        };

        this.currencyText = new BitmapText(" " + (this._config.showSymbol ? this.symbol : BetModel.currency.ISO), textStyle);

        this.addChild(this.moneyText);
        this.addChild(this.centsText);
        this.addChild(this.currencyText);

    }

    get style() {
        return this._config
    }

    set style(newStyle: BitmapMoneyFieldConfig) {
        if(newStyle.tint){
            this._config.tint = newStyle.tint;
            this.moneyText.tint = newStyle.tint;
            this.currencyText.tint = newStyle.tint;
            this.centsText.tint = newStyle.tint;
        }
        this._config.moneyFontSize = newStyle.moneyFontSize;
        this._config.fontFamily = newStyle.fontFamily;
        this.fitToBounds();
    }

    protected resetTextStyle() {
        this.moneyText.fontName =  this._config.fontFamily;
        this.moneyText.fontSize = this._config.moneyFontSize;
        this.centsText.fontName = this._config.fontFamily;
        this.centsText.fontSize = this._config.moneyFontSize * this._config.centsRelativeSize / 100;
        this.currencyText.fontName = this._config.fontFamily;
        this.currencyText.fontSize = this._config.moneyFontSize * this._config.currencyRelativeSize / 100;
    }

    get bounds(): {width: number, height: number} {
        return {
            width: this._config.bounds.width,
            height: this._config.bounds.height
        }
    }

    set bounds(bounds: {width: number, height: number}) {
        this._config.bounds.width = bounds.width;
        this._config.bounds.height = bounds.height;
        if (this.boundsRect) {
            this.boundsRect.width = bounds.width;
            this.boundsRect.height = bounds.height;
        }

        this.fitToBounds();
        this.positionText();
    }

    get value(): number {
        return this.money;
    }

    set align(value: string) {
        this._config.align = value;
        this.fitToBounds();
        this.positionText();
    }

    private fitToBounds() {
        if (!this._config.shouldFitToBounds) {
            return;
        }
        this.resetTextStyle();
        let currencyCompensation = 0;
        if (this._config.alignOnlyMoney && this._config.align == "center") {
            currencyCompensation = this.currencyText.width;
        }
        let moneyFontSize: number = this.moneyText.fontSize ;
        let centsFontSize: number =  this.moneyText.fontSize as number;
        let currencyFontSize: number =  this.moneyText.fontSize as number;

        const coefCurrency: number = currencyFontSize / moneyFontSize;
        const coefCents: number = centsFontSize / moneyFontSize;

        const changeFontSizes = () => {
            this.moneyText.fontSize = moneyFontSize;
            centsFontSize = Math.floor(moneyFontSize * coefCents);
            this.centsText.fontSize = centsFontSize;
            currencyFontSize = Math.floor(moneyFontSize * coefCurrency);

            this.moneyText.dirty = true;
            this.centsText.dirty = true;

            if (!this._config.keepCurrencySize) {
                this.currencyText.fontSize = currencyFontSize;
                this.currencyText.dirty = true;
            }
        };

        if (this.textHeight >= this._config.bounds.height) {
            moneyFontSize = Math.floor(this._config.bounds.height * moneyFontSize / this.textHeight);
            changeFontSizes();
        }
        if (this.textWidth >= this._config.bounds.width - currencyCompensation) {
            if (this._config.keepCurrencySize) {
                moneyFontSize = Math.floor((this._config.bounds.width - this.currencyText.width) * moneyFontSize / this.textWidth);
            } else {
                moneyFontSize = Math.floor(this._config.bounds.width * moneyFontSize / this.textWidth);
            }

            changeFontSizes();
        }
        if (this._config.minFontSize > moneyFontSize) {
            moneyFontSize = this._config.minFontSize;
            changeFontSizes();
        }

    }

    get textWidth() {
        // const moneyWidth = this.moneyText.context.measureText(this.moneyText.text).width;
        // const centsWidth = this.centsText.visible ? this.centsText.context.measureText(this.centsText.text).width : 0;
        // const currencyWidth = this.currencyText.context.measureText(this.currencyText.text).width;
        const moneyWidth = this.moneyText.width;
        const centsWidth = this.centsText.visible ? this.centsText.width : 0;
        const currencyWidth = this.currencyText.visible ? this.currencyText.width : 0;
        return moneyWidth + centsWidth + currencyWidth;
    }

    get textHeight() {
        return Math.max(
            this.moneyText.height,
            this.centsText.height,
            !this._config.keepCurrencySize && this.currencyText.height || 0
        );
    }

    protected positionText() {
        const textWidth = this.textWidth;
        const moneyTextHeight = this.moneyText.textHeight;
        const centsTextHeight = this.centsText.textHeight;
        const currencyTextHeight = this.currencyText.textHeight;

        this.moneyText.y = (this._config.bounds.height - this.moneyText.textHeight) / 2;
        if (this._config.align == "left") {
            this.moneyText.x = 0;
        } else if (this._config.align == "right") {
            this.moneyText.x = this._config.bounds.width - textWidth;
        } else {
            this.moneyText.x = (this._config.bounds.width - textWidth) / 2;
            if (this._config.alignOnlyMoney && this.centsText.visible == false) {
                this.moneyText.x += this.currencyText.width / 2;
            }
        }

        if (this.centsText.visible) {
            this.centsText.x = this.moneyText.x + this.moneyText.width;
            this.centsText.y = this.moneyText.y + moneyTextHeight - centsTextHeight;
        }
        if (this._config.showIdentifier) {
            const visibleText = this.centsText.visible ? this.centsText : this.moneyText;
            this.currencyText.x = visibleText.x + visibleText.width;
            this.currencyText.y = this.moneyText.y + moneyTextHeight - currencyTextHeight;
        } else {
            this.currencyText.text = "";
            this.currencyText.visible = false;
        }
    }

    set value(value: number) {
        if (!this.useCoins) {
            value *= BetModel.currencyRate;
        }

        this.money = value;
        this.moneyText.text = this.moneyString;
        this.centsText.text = this.centsString;
        if (this._config.hideWhenZero && value == 0) {
            this.moneyText.visible = false;
            this.centsText.visible = false;
            this.currencyText.visible = false;
        } else {
            this.moneyText.visible = true;
            this.centsText.visible = this.shouldCentsHaveValue();
        }
        this.fitToBounds();
        this.positionText();
    }

    get moneyString() {
        let result;
        if (BetModel.isDisplayingCredits) {
            // if (false) {
            result = Math.floor(this.money * BetModel.denomination);
        } else {
            result = (this.money / App.config.subUnits).toFixed(BetModel.currency.decimals);
            const moneyParts = result.split(".");
            const digits = moneyParts[0].split('');

            if (this._config.spaceBetweenThousands) {
                let counter = 3;
                let digitIndex = digits.length;
                while (--digitIndex > 0) {
                    counter--;
                    if (counter == 0) {
                        counter = 3;
                        digits.splice(digitIndex, 0, this._config.symbolBetweenThousands);
                    }
                }
            }

            result = digits.join('') + (this.shouldCentsHaveValue() ? this._config.decimalSeparatorSymbol : "");
        }

        if (this._config.showIdentifier) {
            if (this._config.prependSymbol) {
                result = (this._config.showSymbol ? this.symbol : BetModel.currency.ISO) + " " + result;
                this.currencyText.visible = false;
            }
        }

        return result;
    }

    get centsString() {
        let result = "";
        if (this.shouldCentsHaveValue()) {
            result = (this.money / App.config.subUnits)
                .toFixed(BetModel.currency.decimals)
                .split(".")[1];
        }
        return result;
    }

    private shouldCentsHaveValue(): boolean {
        // Destructure the relevant configuration for easier access
        const config = Configs?.components?.moneyFields;
    
        // Check if all bitmap money fields should hide cents
        const hideAll = config?.hideAllBitmapMoneyFields;
    
        // Check if cents should be hidden for this specific money field and the value is an integer
        const hideCentsForField = config?.hideCentsForMoneyFields?.includes(this._config.bounds.id) && isInteger(this.value);
    
        // Check if there are specific conditions to hide cents for this field
        const hideCentsCondition = config?.hideCentsConditions?.fields.includes(this._config.bounds.id) &&
                                   isInteger(this.value) &&
                                   config?.hideCentsConditions?.condition(this);
        
        // If any of the conditions to hide cents are met, return false
        if (hideAll || hideCentsForField || hideCentsCondition) {
            return false;
        }
    
        // Default case: return true if not displaying credits and currency supports decimals
        return !BetModel.isDisplayingCredits && BetModel.currency.decimals > 0;
    }
    

    public  rescale(bounds: IObjectConfig) {
        Layout.setY(this, bounds.y);
        Layout.setX(this, bounds.x);
        Layout.testSetWidth(this, bounds.width);
    }


}
